import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CONFIG } from '../../config';
import { PaymentSource } from '../../constants';
import { loadAdyenScript } from '../../lib/loadScript';
import { encryptCard } from '../../lib/encryptCard';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary } from '../useOrderSummary';
import { UpdateOrderAction } from '../useUpdateOrder';
import { useSecondPaymentCompletedTracking } from '../useSecondPaymentCompletedTracking';
import { PLACE_AFFIRM_SECOND_PAYMENT_ORDER_MUTATION } from './placeAffirmSecondPaymentOrderMutation.rest';
import { AffirmSecondPaymentResponse } from './useAffirmSecondPayment';

const CARD_EXPIRY_REGEXP = /^([0-9]{2})([0-9]{2})$/;

export const usePlaceAffirmSecondPaymentOrderMutation = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);
  const {
    country: { alpha2Code }
  } = useCountry();
  const { trackSecondPaymentCompleted } = useSecondPaymentCompletedTracking();

  const selectedAddressId = orderSummary?.attributes.selectedAddressId;
  const shippingMethodId = orderSummary?.attributes.shippingMethodId;

  const [mutate, { loading, error }] = useMutation(PLACE_AFFIRM_SECOND_PAYMENT_ORDER_MUTATION);

  useEffect(() => {
    loadAdyenScript();
  }, []);

  const handleAffirmSecondPayment = useCallback(
    async (response: AffirmSecondPaymentResponse) => {
      if (!selectedAddressId)
        return Promise.reject('PlaceAffirmSecondPaymentOrderMutation: no address selected');
      if (!shippingMethodId)
        return Promise.reject('PlaceAffirmSecondPaymentOrderMutation: no shipping method selected');
      if (!orderSummary)
        return Promise.reject('PlaceAffirmSecondPaymentOrderMutation: no orderSummary');

      const result = await mutate({
        variables: {
          orderId: orderSummary.id,
          input: {
            country_code: alpha2Code,
            id: orderSummary.id,
            data: {
              id: orderSummary.id,
              type: 'orders',
              action_type: UpdateOrderAction.Charge,
              attributes: {
                country_code: alpha2Code,
                payment_source: PaymentSource.Affirm,
                shipment_method: shippingMethodId,
                payment_summary: {
                  encrypted_card_data: {
                    encrypted_data: encryptCard({
                      holderName: response.cardholder_name,
                      number: response.number,
                      expiryMonth:
                        CONFIG.ENVIRONMENT === 'prod'
                          ? response.expiration.match(CARD_EXPIRY_REGEXP)?.[1] || ''
                          : '10',
                      expiryYear:
                        CONFIG.ENVIRONMENT === 'prod'
                          ? `20${response.expiration.match(CARD_EXPIRY_REGEXP)?.[2]}`
                          : '2020',
                      cvc: CONFIG.ENVIRONMENT === 'prod' ? response.cvv : '737'
                    })
                  },
                  affirm_checkout_token: response.checkout_token,
                  billing_address: {
                    address_line1: response.billing_address.line1,
                    address_line2: response.billing_address.line2,
                    city: response.billing_address.city,
                    state: response.billing_address.state,
                    country_code: alpha2Code,
                    postal_code: response.billing_address.zipcode
                  }
                }
              }
            }
          }
        }
      });

      trackSecondPaymentCompleted(result);
      return result;
    },
    [
      shippingMethodId,
      selectedAddressId,
      alpha2Code,
      orderSummary,
      trackSecondPaymentCompleted,
      mutate
    ]
  );

  return [handleAffirmSecondPayment, { loading, error }] as const;
};
